// src/pages/billing/CreateInvoice.js
// TO-8: AMC pre-fill added
// - Reads ?amc=contractId from URL
// - Fetches amc_contract → pre-fills client, site, amount, line item
// - On save → updates amc_contract: next_invoice_date + last_invoice_id + last_invoice_date
// - Invoice saved with is_amc: true + amc_contract_id

import { useEffect, useState } from "react";
import {
  collection, getDocs, query, where,
  addDoc, serverTimestamp, doc, getDoc, updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { calcNextInvoiceDate } from "../services/AMCContracts";
import { useCompany } from "../../context/CompanyContext";
import {
  QuickCreateButton,
  QuickCreateModal,
  QuickCreateClient,
  QuickCreateSite,
} from "../../components/quickcreate";

export default function CreateInvoice() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const { company } = useCompany(); // for place_of_supply — same-state assumption, see Company Profile's own state field
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const location        = useLocation();

  const editId       = searchParams.get("id");    // edit existing invoice
  const amcId        = searchParams.get("amc");   // ── TO-8: generate from AMC contract
  const serviceState = location.state?.from_service ? location.state : null;

  const [clients,  setClients]  = useState([]);
  const [projects, setProjects] = useState([]);
  const [sites,    setSites]    = useState([]);
  const [saving,        setSaving]        = useState(false);
  const [dupWarning,    setDupWarning]    = useState(null);
  const [qcOpen,        setQcOpen]        = useState(null); // "client" | "site" // existing invoice if duplicate

  // ── TO-8: AMC contract state ──────────────────────────────────────────────
  const [amcContract, setAmcContract] = useState(null);

  const [form, setForm] = useState({
    invoice_no:      "",
    client_id:       "",
    client_name:     "",
    client_gstin:    "", // pulled from the client's own record when selected — printed on invoice for GST-registered clients
    place_of_supply: "", // derived from Company Profile's own state (intra-state assumption, per Constitution scope) — see client selection handler
    project_id:      "",
    project_name:    "",
    site_id:         "",
    site_name:       "",
    service_task_id: "",
    // Every invoice is now a single, consistent "Tax Invoice" document —
    // the old is_gst_invoice toggle (which produced two different document
    // types: "Tax Invoice" vs "Cash Receipt") is REMOVED. That toggle was
    // an audit risk — a "Cash Receipt" with no tax language on it, sitting
    // alongside GST invoices, looks like deliberate tax-invoice avoidance
    // under scrutiny. Now: ONE document format always. Individual line
    // items can still have 0% tax (client fills tax_rate per item), but
    // the document itself always looks like a proper Tax Invoice with
    // HSN codes and CGST/SGST columns, whatever the per-item rate is.
    discount:        0,
    paid_amount:     0,
    notes:           "",
    due_date:        "",
    payment_mode:    "Cash", // operational field only — does NOT affect tax/invoice format
  });

  // Default line item shape — hsn_code + tax_rate are now PER ITEM (client's
  // explicit ask: "har item k aage tax ka option hona chahiye, jo bhi hoga
  // client fill karega"). taxable_amount/cgst_amount/sgst_amount/amount are
  // all derived — see updateLineItem()/recalcLineItem() below.
  // source_stage_item_id — set ONLY when this row was imported via
  // "Import from Project" (see importFromProject() below). Used purely to
  // detect "already invoiced" duplicates on a LATER invoice for the same
  // project — never written back to project_material_stages itself
  // (that collection stays exclusively governed by the Material Control
  // engine, per the Constitution's single-governed-engine rule).
  const blankLineItem = () => ({
    item_name: "", description: "", hsn_code: "",
    qty: 1, unit: "Nos", rate: 0, tax_rate: 0,
    taxable_amount: 0, cgst_amount: 0, sgst_amount: 0, amount: 0,
    source_stage_item_id: "",
  });

  const [lineItems, setLineItems] = useState([blankLineItem()]);

  // "Import from Project" — panel state
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [importCandidates, setImportCandidates] = useState([]); // [{key, item_name, unit, qty, hsn_code, source_stage_item_id, alreadyInvoicedIn}]
  const [importSelected, setImportSelected] = useState({}); // { key: true }
  const [loadingImport, setLoadingImport] = useState(false);

  // Place of Supply is intra-state by design (this app's invoicing has no
  // IGST branch yet, per the Constitution's stated scope) — defaults to the
  // company's own registered state as soon as it's available, but stays
  // freely editable in case a specific job genuinely differs.
  useEffect(() => {
    if (company?.state && !form.place_of_supply) {
      setForm(f => ({ ...f, place_of_supply: company.state }));
    }
  }, [company?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── MAIN useEffect ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!company_id) return;
    fetchClients();

    if (editId) {
      loadInvoiceForEdit(editId);
    } else if (amcId) {
      // ── TO-8: AMC pre-fill ──────────────────────────────────────────────
      loadAMCContract(amcId);
    } else {
      generateInvoiceNumber();
      // Pre-fill from chargeable service — now supports a full line-item
        // breakdown (e.g. "Gas Charging" ₹2000, "Cleaning & Leak Check"
        // ₹2000) coming from ServiceDetail.js's Close Job form, instead of
        // a single lump-sum amount. Falls back to the old single-amount
        // shape if a caller ever sends chargeable_amount directly (kept
        // for backward compat, should not happen from current callers).
        if (serviceState) {
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 30);
          const dueDateStr = dueDate.toISOString().split("T")[0];
          setForm(prev => ({
            ...prev,
            client_id:       serviceState.client_id   || "",
            client_name:     serviceState.client_name || "",
            project_id:      serviceState.project_id   || "",
            project_name:    serviceState.project_name || "",
            site_id:         serviceState.site_id     || "",
            site_name:       serviceState.site_name   || "",
            service_task_id: serviceState.service_id  || "",
            due_date:        dueDateStr,
            // Cash already collected at site (e.g. technician took ₹8,500
            // on the spot) — pre-fills Paid so the invoice's balance is
            // correct from the moment it's created, no separate "Record
            // Payment" step needed for money that's already in hand.
            paid_amount:     Number(serviceState.already_collected_amount || 0),
          }));
          if (serviceState.client_id) {
            fetchSites(serviceState.client_id);
            fetchProjects(serviceState.client_id);
          }

          if (Array.isArray(serviceState.close_line_items) && serviceState.close_line_items.length > 0) {
            setLineItems(serviceState.close_line_items.map(li => recalcLineItem({
              ...blankLineItem(),
              item_name:   li.description || "Service Item",
              description: serviceState.job_type || "Service",
              qty:         1,
              unit:        "Job",
              rate:        Number(li.amount || 0),
            })));
          } else if (serviceState.chargeable_amount > 0) {
            // Legacy fallback — single lump-sum amount, no breakdown given
            setLineItems([recalcLineItem({
              ...blankLineItem(),
              item_name:   `Service — ${serviceState.service_complaint || "HVAC Service"}`,
              description: serviceState.job_type || "Service",
              qty:         1,
              unit:        "Job",
              rate:        Number(serviceState.chargeable_amount),
            })]);
          }
        }

        // 🆕 Pre-fill from Project
        const projectState = location.state?.from_project ? location.state : null;
        if (projectState && !serviceState) {
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + 30);
          const dueDateStr = dueDate.toISOString().split("T")[0];
          setForm(prev => ({
            ...prev,
            client_id:    projectState.client_id    || "",
            client_name:  projectState.client_name  || "",
            site_id:      projectState.site_id      || "",
            site_name:    projectState.site_name    || "",
            project_id:   projectState.project_id   || "",
            project_name: projectState.project_name || "",
            due_date:     dueDateStr,
          }));
          if (projectState.client_id) {
            fetchSites(projectState.client_id);
            fetchProjects(projectState.client_id);
          }
          if (projectState.chargeable_amount > 0) {
            setLineItems([recalcLineItem({
              ...blankLineItem(),
              item_name:   `${projectState.service_complaint || projectState.project_name || "Project Work"}`,
              description: "Project Invoice",
              qty:         1,
              unit:        "Job",
              rate:        Number(projectState.chargeable_amount),
            })]);
          }
        }
    }
  }, [company_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── TO-8: Load AMC contract and pre-fill form ─────────────────────────────
  const loadAMCContract = async (contractId) => {
    try {
      const snap = await getDoc(doc(db, "amc_contracts", contractId));
      if (!snap.exists()) { alert("AMC contract not found"); navigate("/billing/dashboard"); return; }
      const c = { id: snap.id, ...snap.data() };

      // Multi-tenant guard
      if (c.company_id !== company_id) { alert("Access denied"); navigate("/billing/dashboard"); return; }

      setAmcContract(c);

      // Generate invoice number
      await generateInvoiceNumber();

      // Auto due date = next_invoice_date + 30 days
      const dueDate = new Date(c.next_invoice_date || new Date());
      dueDate.setDate(dueDate.getDate() + 30);
      const dueDateStr = dueDate.toISOString().split("T")[0];

      // Pre-fill form
      setForm(prev => ({
        ...prev,
        client_id:   c.client_id   || "",
        client_name: c.client_name || "",
        site_id:     c.site_id     || "",
        site_name:   c.site_name   || "",
        due_date:    dueDateStr,
        notes:       `AMC Contract: ${c.contract_no} — ${c.description || c.client_name}`,
      }));

      // Pre-fill line item with AMC details
      const periodLabel = {
        monthly:   `Monthly`,
        quarterly: `Quarterly`,
        yearly:    `Annual`,
      }[c.frequency] || c.frequency;

      const invoiceMonth = c.next_invoice_date
        ? new Date(c.next_invoice_date).toLocaleString("en-IN", { month: "long", year: "numeric" })
        : "";

      setLineItems([recalcLineItem({
        ...blankLineItem(),
        item_name:   `AMC — ${periodLabel} Maintenance`,
        description: `${c.description || c.client_name}${invoiceMonth ? ` — ${invoiceMonth}` : ""}`,
        qty:         1,
        unit:        "Job",
        rate:        Number(c.amount || 0),
      })]);

      // Load sites for client
      if (c.client_id) {
        fetchSites(c.client_id);
        fetchProjects(c.client_id);
      }
    } catch (err) {
      console.error("[CreateInvoice] loadAMCContract error:", err);
      alert("Failed to load AMC contract");
    }
  };

  // ── Invoice number ────────────────────────────────────────────────────────
  const generateInvoiceNumber = async () => {
    try {
      const snap = await getDocs(
        query(collection(db, "invoices"), where("company_id", "==", company_id))
      );
      const invoiceNo = `INV-${String(snap.size + 1).padStart(4, "0")}-${Date.now().toString().slice(-4)}`;
      setForm(prev => ({ ...prev, invoice_no: invoiceNo }));
    } catch (err) { console.error("Invoice number error:", err); }
  };

  // ── Load invoice for edit ─────────────────────────────────────────────────
  const loadInvoiceForEdit = async (invoiceId) => {
    try {
      const snap = await getDoc(doc(db, "invoices", invoiceId));
      if (!snap.exists()) return;
      const d = snap.data();
      if (d.company_id !== company_id) {
        alert("Access denied — this invoice belongs to a different company");
        navigate("/billing/invoices");
        return;
      }
      setForm({
        invoice_no:      d.invoice_no      || "",
        client_id:       d.client_id       || "",
        client_name:     d.client_name     || "",
        client_gstin:    d.client_gstin    || "",
        place_of_supply: d.place_of_supply || "",
        project_id:      d.project_id      || "",
        project_name:    d.project_name    || "",
        site_id:         d.site_id         || "",
        site_name:       d.site_name       || "",
        service_task_id: d.service_task_id || "",
        discount:        d.discount        ?? 0,
        paid_amount:     d.paid_amount     ?? 0,
        notes:           d.notes           || "",
        due_date:        d.due_date        || "",
        payment_mode:    d.payment_mode    || "",
      });
      if (d.line_items) {
        // Legacy invoices (created before HSN/per-item-tax existed) have
        // line items missing hsn_code/tax_rate/taxable_amount/cgst_amount/
        // sgst_amount — backfill safe defaults so editing an old invoice
        // doesn't crash, then recompute via recalcLineItem() so amount/
        // tax figures are consistent with the current schema.
        setLineItems(d.line_items.map(li => recalcLineItem({
          item_name: li.item_name || "",
          description: li.description || "",
          hsn_code: li.hsn_code || "",
          qty: li.qty ?? 1,
          unit: li.unit || "Nos",
          rate: li.rate ?? 0,
          // Old invoices had one invoice-level tax_percent — carry it onto
          // every line item as a one-time migration-on-open, so editing an
          // old GST invoice keeps behaving the way it always did.
          tax_rate: li.tax_rate ?? (d.is_gst_invoice ? Number(d.tax_percent || 0) : 0),
          source_stage_item_id: li.source_stage_item_id || "",
        })));
      }
      if (d.client_id)  { fetchProjects(d.client_id); fetchSites(d.client_id); }
    } catch (err) { console.error("Load invoice error:", err); }
  };

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchClients = async () => {
    const snap = await getDocs(query(
      collection(db, "clients"), where("company_id", "==", company_id)
    ));
    setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const fetchProjects = async (clientId) => {
    const snap = await getDocs(query(
      collection(db, "projects"),
      where("company_id", "==", company_id),
      where("client_id",  "==", clientId)
    ));
    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setProjects(list);
    return list;
  };

  const fetchSites = async (clientId) => {
    if (!clientId) return [];
    try {
      const snap = await getDocs(query(
        collection(db, "sites"),
        where("company_id", "==", company_id),
        where("client_id",  "==", clientId)
      ));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSites(list);
      return list;
    } catch (err) { console.error("fetchSites error:", err); return []; }
  };

  const fetchSitesByProject = async (projectId) => {
    if (!projectId) return [];
    try {
      const snap = await getDocs(query(
        collection(db, "sites"),
        where("company_id", "==", company_id),
        where("project_id", "==", projectId)
      ));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (!snap.empty) setSites(list);
      return list;
    } catch (err) { console.error("fetchSitesByProject error:", err); return []; }
  };

  // ── Line item helpers ─────────────────────────────────────────────────────
  // Recomputes taxable_amount/cgst_amount/sgst_amount/amount for ONE line
  // item, from its own qty/rate/tax_rate — completely independent of every
  // other line item and of the invoice-level discount. This is what lets
  // each item carry its own tax rate (0–28%, client fills it) while still
  // producing a single, internally-consistent Tax Invoice document.
  const recalcLineItem = (item) => {
    const qty  = Number(item.qty) || 0;
    const rate = Number(item.rate) || 0;
    const taxRate = Number(item.tax_rate) || 0;
    const taxable = qty * rate;
    const cgst = (taxable * (taxRate / 2)) / 100;
    const sgst = (taxable * (taxRate / 2)) / 100;
    return {
      ...item,
      taxable_amount: taxable,
      cgst_amount:    cgst,
      sgst_amount:    sgst,
      amount:         taxable + cgst + sgst,
    };
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = recalcLineItem({ ...updated[index], [field]: value });
    setLineItems(updated);
  };
  const addLineItem    = () => setLineItems([...lineItems, blankLineItem()]);
  const removeLineItem = (index) => {
    if (lineItems.length === 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // ── Calculations ──────────────────────────────────────────────────────────
  // Per-item tax already computed in each lineItem (cgst_amount/sgst_amount,
  // via recalcLineItem()) — invoice-level totals are just sums of those.
  // Discount is applied AFTER tax, as a straight deduction from the grand
  // total — it does NOT reduce any item's taxable value or recompute any
  // item's tax. This keeps each line item's tax figure exactly what its
  // own tax_rate produces, with no cross-item interaction — simplest and
  // safest for audit purposes (the printed tax breakdown always matches
  // qty × rate × tax_rate for every single row).
  const subtotal    = lineItems.reduce((s, i) => s + Number(i.taxable_amount || 0), 0);
  const totalCgst   = lineItems.reduce((s, i) => s + Number(i.cgst_amount || 0), 0);
  const totalSgst   = lineItems.reduce((s, i) => s + Number(i.sgst_amount || 0), 0);
  const taxAmount   = totalCgst + totalSgst;
  const totalAmount = subtotal + taxAmount - Number(form.discount);
  const balanceAmount = totalAmount - Number(form.paid_amount);
  const paymentStatus = balanceAmount <= 0 ? "Paid" : form.paid_amount > 0 ? "Partial" : "Pending";

// ── IMPORT FROM PROJECT ────────────────────────────────────────────────
  // Reads-only from project_material_stages + equipment_instances (BOTH
  // already-existing, governed-elsewhere collections — Material Control
  // and Equipment Registry own their writes; this function NEVER writes
  // to either). Pulls item_name + unit + qty_issued (ACTUAL delivered
  // quantity, not the planned qty_budgeted — billing should reflect what
  // really went out, not what was originally planned) into the invoice's
  // line items. Rate is DELIBERATELY left at 0 — cost_rate on a material
  // stage is Zeno's internal purchase cost, never the client-facing
  // selling price, and the two must never be conflated. Manager always
  // types the actual billing rate.
  const openImportPanel = async () => {
    if (!form.project_id) { alert("Select a Project first"); return; }
    setLoadingImport(true);
    setShowImportPanel(true);
    try {
      const candidates = [];

      // Material stages
      const stageSnap = await getDocs(query(
        collection(db, "project_material_stages"),
        where("company_id", "==", company_id),
        where("project_id", "==", form.project_id)
      ));
      stageSnap.docs.forEach(stageDoc => {
        const stage = stageDoc.data();
        (stage.material_budget || []).forEach((mb, idx) => {
          const qtyIssued = Number(mb.qty_issued || 0);
          if (qtyIssued <= 0) return; // nothing actually delivered yet — nothing to bill
          candidates.push({
            key: `stage_${stageDoc.id}_${idx}`,
            source_stage_item_id: `${stageDoc.id}_${mb.item_id || idx}`,
            item_name: mb.item_name || "",
            unit: mb.unit || "Nos",
            qty: qtyIssued,
            hsn_code: "", // Item Master HSN lookup — see below
            item_id: mb.item_id || "",
          });
        });
      });

      // Equipment issued to this project
      const equipSnap = await getDocs(query(
        collection(db, "equipment_instances"),
        where("company_id", "==", company_id),
        where("current_project_id", "==", form.project_id)
      ));
      equipSnap.docs.forEach(d => {
        const eq = d.data();
        candidates.push({
          key: `equip_${d.id}`,
          source_stage_item_id: `equip_${d.id}`,
          item_name: eq.item_name || "",
          unit: eq.unit || "Nos",
          qty: 1,
          hsn_code: "",
          item_id: "",
        });
      });

      // Best-effort HSN lookup from Item Master (items collection) — if
      // the item doesn't carry an hsn_code field, leave it blank; the
      // manager fills it in the invoice form either way.
      const itemIds = [...new Set(candidates.filter(c => c.item_id).map(c => c.item_id))];
      if (itemIds.length > 0) {
        const itemsSnap = await getDocs(query(
          collection(db, "items"),
          where("company_id", "==", company_id)
        ));
        const hsnMap = {};
        itemsSnap.docs.forEach(d => {
          const it = d.data();
          if (it.item_id) hsnMap[it.item_id] = it.hsn_code || "";
        });
        candidates.forEach(c => { if (c.item_id && hsnMap[c.item_id]) c.hsn_code = hsnMap[c.item_id]; });
      }

      // Duplicate-check — look at OTHER invoices already created for this
      // same project, see which source_stage_item_id values they already
      // used. Read-only check against the invoices collection only —
      // never touches project_material_stages/equipment_instances.
      const otherInvoicesSnap = await getDocs(query(
        collection(db, "invoices"),
        where("company_id", "==", company_id),
        where("project_id", "==", form.project_id)
      ));
      const alreadyInvoiced = {}; // source_stage_item_id -> invoice_no
      otherInvoicesSnap.docs.forEach(d => {
        const inv = d.data();
        if (editId && d.id === editId) return; // ignore the invoice we're currently editing
        (inv.line_items || []).forEach(li => {
          if (li.source_stage_item_id) alreadyInvoiced[li.source_stage_item_id] = inv.invoice_no;
        });
      });
      candidates.forEach(c => { c.alreadyInvoicedIn = alreadyInvoiced[c.source_stage_item_id] || ""; });

      setImportCandidates(candidates);
      setImportSelected({});
    } catch (err) {
      console.error("[CreateInvoice] openImportPanel error:", err);
      alert("Failed to load project items: " + err.message);
    } finally {
      setLoadingImport(false);
    }
  };

  const confirmImport = () => {
    // Skip anything already present in THIS invoice's own line items (by
    // source_stage_item_id) — prevents the exact duplicate-row bug where
    // opening the Import panel a second time and re-confirming the same
    // selections added the same items again.
    const alreadyInForm = new Set(
      lineItems.filter(li => li.source_stage_item_id).map(li => li.source_stage_item_id)
    );
    const chosen = importCandidates.filter(c => importSelected[c.key] && !alreadyInForm.has(c.source_stage_item_id));
    if (chosen.length === 0) { setShowImportPanel(false); return; }
    const newItems = chosen.map(c => recalcLineItem({
      ...blankLineItem(),
      item_name: c.item_name,
      unit: c.unit,
      qty: c.qty,
      hsn_code: c.hsn_code,
      rate: 0, // NEVER auto-filled — manager always sets the actual billing rate
      source_stage_item_id: c.source_stage_item_id,
    }));
    // Replace a single still-blank starter row if that's all that's there,
    // otherwise append after existing rows.
    setLineItems(prev => {
      const isSingleBlank = prev.length === 1 && !prev[0].item_name.trim();
      return isSingleBlank ? newItems : [...prev, ...newItems];
    });
    setShowImportPanel(false);
  };

  // ── Save invoice ──────────────────────────────────────────────────────────
  const saveInvoice = async (forceCreate = false) => {
    if (!form.client_id) { alert("Select a client"); return; }
    if (!lineItems.some(i => i.item_name.trim())) { alert("Add at least one line item"); return; }
    if (lineItems.some(i => i.item_name.trim() && Number(i.rate) <= 0)) {
      alert("All line items must have a rate > 0"); return;
    }

    // ── Duplicate check — only for new invoices linked to a service task ──
    if (!editId && !forceCreate && form.service_task_id) {
      try {
        const dupSnap = await getDocs(query(
          collection(db, "invoices"),
          where("company_id",     "==", company_id),
          where("service_task_id","==", form.service_task_id)
        ));
        if (!dupSnap.empty) {
          const existing = dupSnap.docs[0].data();
          setDupWarning({
            invoice_no: existing.invoice_no || dupSnap.docs[0].id,
            client_name: existing.client_name || "",
            total_amount: existing.total_amount || 0,
          });
          return; // stop — show warning modal
        }
      } catch (err) {
        console.warn("[CreateInvoice] duplicate check failed (non-blocking):", err);
      }
    }

    // ── Zero amount block ──────────────────────────────────────────────────
    if (!editId && totalAmount <= 0) {
      alert("Invoice total must be greater than ₹0");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        ...form,
        company_id,
        line_items:      lineItems, // each item carries its own hsn_code/tax_rate/taxable_amount/cgst_amount/sgst_amount
        subtotal,
        total_cgst:      totalCgst,
        total_sgst:      totalSgst,
        tax_amount:      taxAmount,
        total_amount:    totalAmount,
        balance_amount:  balanceAmount,
        payment_status:  paymentStatus,
        invoice_status:  "Approved",
        invoice_date:    new Date(),
        created_by:      user.uid || user.id || "",
        created_by_name: user.name || "",
        approved_by:     user.uid || user.id || "",
        updated_at:      serverTimestamp(),
        is_active:       true,
        // ── TO-8: AMC fields ──────────────────────────────────────────────
        is_amc:           Boolean(amcContract),
        amc_contract_id:  amcContract?.id || "",
      };

      let savedInvoiceId = editId;

      if (editId) {
        await updateDoc(doc(db, "invoices", editId), payload);
      } else {
        const invoiceRef = await addDoc(collection(db, "invoices"), {
          ...payload,
          created_at: serverTimestamp(),
        });
        savedInvoiceId = invoiceRef.id;

        // financial_transactions entry — project_id/project_name included
        // so the Costing Card (project_summary, via the existing
        // onFinancialTransactionCreate trigger) can attribute this income
        // to the correct project. Previously this field was collected in
        // the form but never actually written here.
        // Financial transaction — project_id/project_name included so this
      // later payment (which may arrive weeks/months after the original
      // invoice, e.g. credit-based jobs) is ALSO correctly attributed to
      // the project in project_summary via onFinancialTransactionCreate.
      // This is exactly the gap that previously made later-arriving
      // payments invisible to any per-project costing view.
     // financial_transactions entry — project_id/project_name included
        // so the Costing Card (project_summary, via the existing
        // onFinancialTransactionCreate trigger) can attribute this income
        // to the correct project. Previously this field was collected in
        // the form but never actually written here.
        // Income is recognized on ACTUAL cash collected, not on the full
        // billed amount — matches how every other domain in this app
        // records cost/income (Material/Equipment/Subcontractor: at the
        // actual issue/return event, not the full BOQ budget). Writing
        // the FULL totalAmount here, unconditionally, double-counted any
        // money that later gets recorded again via InvoiceDetail.js's
        // Record Payment/Mark as Paid (which correctly write the exact
        // cash amount received each time) — e.g. a ₹5,90,000 invoice with
        // a later ₹4,00,000 payment recorded was inflating Total Income
        // to ₹9,90,000 instead of the true ₹4,00,000 collected so far.
        // Only write this entry at all if something was ALREADY collected
        // at creation time (e.g. cash collected on the spot for a service
        // job, pre-filled into paid_amount) — a fresh invoice with 0
        // collected creates no premature income entry; all its income
        // will come from actual Record Payment/Mark as Paid events later.
        const collectedAtCreation = Number(form.paid_amount || 0);
        if (collectedAtCreation > 0) {
          await addDoc(collection(db, "financial_transactions"), {
            company_id,
            type:            "income",
            sub_type:        "invoice",
            reference_id:    form.invoice_no,
            reference_no:    form.invoice_no,
            client_id:       form.client_id,
            client_name:     form.client_name,
            project_id:      form.project_id   || "",
            project_name:    form.project_name || "",
            site_id:         form.site_id      || "",
            site_name:       form.site_name    || "",
            service_task_id: form.service_task_id || "",
            amc_contract_id: amcContract?.id || "",
            amount:          collectedAtCreation,
            balance_amount:  balanceAmount,
            paid_amount:     collectedAtCreation,
            payment_mode:    form.payment_mode || "",
            status:          balanceAmount <= 0 ? "paid" : "payment_pending",
            description:     `Invoice ${form.invoice_no} — ${form.client_name}${amcContract ? ` (AMC)` : ""} (collected at creation)`,
            created_by:      user.uid || user.id || "",
            created_by_name: user.name || "",
            created_at:      serverTimestamp(),
          });
        }

        // ── TO-8: Advance AMC contract next_invoice_date ──────────────────
        if (amcContract) {
          const nextDate = calcNextInvoiceDate(
            amcContract.next_invoice_date || amcContract.start_date,
            amcContract.frequency
          );
          await updateDoc(doc(db, "amc_contracts", amcContract.id), {
            next_invoice_date:  nextDate,
            last_invoice_id:    savedInvoiceId,
            last_invoice_date:  new Date().toISOString().split("T")[0],
            updated_at:         serverTimestamp(),
          });
          console.log("[CreateInvoice] AMC next_invoice_date advanced to:", nextDate);
        }

        // ── Notify invoice created (AMC + regular) ──────────────────────
        try {
          const { notifyInvoiceCreated, markNotificationReadByReference } =
            await import("../../services/notificationService");

          await notifyInvoiceCreated({
            invoice_id:     savedInvoiceId,
            invoice_number: form.invoice_no,
            client_name:    form.client_name,
            amount:         totalAmount,
            company_id,
          });

          // 🆕 Billing Pending notification hatao (service se invoice ban gayi)
          if (form.service_task_id) {
            await markNotificationReadByReference(
              form.service_task_id,
              "billing_pending",
              company_id
            );
          }
        } catch (notifErr) {
          console.warn("[CreateInvoice] notification failed (non-blocking):", notifErr);
        }
      }

      alert(editId ? "Invoice updated ✅" : "Invoice created ✅");
      navigate("/billing/invoices");
    } catch (err) {
      console.error("Invoice save error:", err);
      alert("Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">
            {editId
              ? "Edit Invoice"
              : amcContract
              ? "Generate AMC Invoice"
              : "Create Invoice"}
          </h1>
          {/* TO-8: AMC banner */}
          {amcContract && (
            <p className="text-sm text-purple-600 mt-0.5 font-medium">
              🔄 AMC Contract: {amcContract.contract_no} —{" "}
              {amcContract.frequency?.charAt(0).toUpperCase() + amcContract.frequency?.slice(1)} •
              Due: {amcContract.next_invoice_date}
            </p>
          )}
        </div>
        <button onClick={() => navigate(amcContract ? "/billing/dashboard" : "/billing/invoices")}
          className="text-gray-500 hover:text-gray-800 text-sm">← Back</button>
      </div>

      {/* HEADER FIELDS — same width as Line Items and Totals cards below,
          for a consistent card-width layout across the whole page. */}
      <div className="bg-white p-5 rounded-xl shadow space-y-4">
        <div className="grid md:grid-cols-3 gap-4">

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Invoice No</label>
            <input value={form.invoice_no} disabled
              className="border p-2 rounded w-full bg-gray-50 text-sm" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Client *</label>
            <div className="flex gap-2 items-center">
              <select className="border p-2 rounded flex-1 text-sm" value={form.client_id}
                disabled={Boolean(amcContract)}
                onChange={async e => {
                  const c = clients.find(c => c.id === e.target.value);
                  if (!c) return;
                  setForm(f => ({ ...f, client_id: c.id, client_name: c.client_name,
                    client_gstin: c.gstin || "",
                    project_id: "", project_name: "", site_id: "", site_name: "" }));
                  const [projList, siteList] = await Promise.all([fetchProjects(c.id), fetchSites(c.id)]);
                  // Auto-select — only when exactly one option exists, mirrors the
                  // existing autoSelectSiteIfOnlyOne() pattern used elsewhere in the app.
                  setForm(f => ({
                    ...f,
                    project_id:   projList.length === 1 ? projList[0].id : f.project_id,
                    project_name: projList.length === 1 ? (projList[0].project_name || projList[0].project_title) : f.project_name,
                    site_id:      siteList.length === 1 ? siteList[0].id : f.site_id,
                    site_name:    siteList.length === 1 ? (siteList[0].site_name || siteList[0].site_title) : f.site_name,
                  }));
                }}>
                <option value="">Select Client *</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
              </select>
              {!amcContract && <QuickCreateButton onClick={() => setQcOpen("client")} label="Add client" />}
            </div>
            {amcContract && (
              <p className="text-xs text-purple-500 mt-1">Pre-filled from AMC contract</p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Project</label>
            <select className="border p-2 rounded w-full text-sm" value={form.project_id}
              onChange={async e => {
                const p = projects.find(p => p.id === e.target.value);
                if (!p) return;
                setForm(f => ({ ...f, project_id: p.id,
                  project_name: p.project_name || p.project_title,
                  site_id: "", site_name: "" }));
                const siteList = await fetchSitesByProject(p.id);
                if (siteList && siteList.length === 1) {
                  setForm(f => ({ ...f, site_id: siteList[0].id, site_name: siteList[0].site_name || siteList[0].site_title }));
                }
              }}>
              <option value="">Select Project</option>
              {projects.map(p =>
                <option key={p.id} value={p.id}>{p.project_name || p.project_title}</option>
              )}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Site</label>
            <div className="flex gap-2 items-center">
              <select className="border p-2 rounded flex-1 text-sm" value={form.site_id}
                onChange={e => {
                  const s = sites.find(s => s.id === e.target.value);
                  if (!s) return;
                  setForm({ ...form, site_id: s.id, site_name: s.site_name || s.site_title });
                }}>
                <option value="">Select Site</option>
                {sites.map(s =>
                  <option key={s.id} value={s.id}>{s.site_name || s.site_title}</option>
                )}
              </select>
              <QuickCreateButton
                onClick={() => setQcOpen("site")}
                label="Add site"
                disabled={!form.client_id}
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">
              Service Job ID <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input placeholder="Link to chargeable service..."
              value={form.service_task_id}
              onChange={e => setForm({ ...form, service_task_id: e.target.value })}
              className="border p-2 rounded w-full text-sm" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Due Date</label>
            <input type="date" value={form.due_date}
              onChange={e => setForm({ ...form, due_date: e.target.value })}
              className="border p-2 rounded w-full text-sm" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Payment Mode</label>
            <select value={form.payment_mode}
              onChange={e => setForm({ ...form, payment_mode: e.target.value })}
              className="border p-2 rounded w-full text-sm">
              <option value="">Select Mode</option>
              <option value="Cash">Cash</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="Cheque">Cheque</option>
              <option value="UPI">UPI</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Client GSTIN</label>
            <input value={form.client_gstin}
              onChange={e => setForm({ ...form, client_gstin: e.target.value.toUpperCase() })}
              placeholder="Auto-filled from client, editable"
              maxLength={15}
              className="border p-2 rounded w-full text-sm uppercase" />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-500 block mb-1">Place of Supply</label>
            <input value={form.place_of_supply}
              onChange={e => setForm({ ...form, place_of_supply: e.target.value })}
              placeholder="e.g. Uttarakhand"
              className="border p-2 rounded w-full text-sm" />
          </div>

        </div>
      </div>

      {/* LINE ITEMS */}
      <div className="bg-white rounded-xl shadow p-5">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Line Items</h2>
          {form.project_id && (
            <button onClick={openImportPanel}
              className="px-3 py-1.5 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg text-xs font-semibold hover:bg-teal-100">
              📋 Import Items from Project
            </button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed border-collapse">
            <colgroup>
              <col style={{ width: "15%" }} />{/* Item */}
              <col style={{ width: "15%" }} />{/* Description */}
              <col style={{ width: "9%" }} /> {/* HSN/SAC */}
              <col style={{ width: "6%" }} /> {/* Qty */}
              <col style={{ width: "8%" }} /> {/* Unit */}
              <col style={{ width: "9%" }} /> {/* Rate */}
              <col style={{ width: "7%" }} /> {/* Tax % */}
              <col style={{ width: "9%" }} /> {/* Taxable */}
              <col style={{ width: "8%" }} /> {/* CGST */}
              <col style={{ width: "8%" }} /> {/* SGST */}
              <col style={{ width: "8%" }} /> {/* Total */}
              <col style={{ width: "3%" }} /> {/* remove */}
            </colgroup>
            <thead>
              <tr className="bg-gray-50">
                <th className="p-3 text-left border-b font-semibold">Item</th>
                <th className="p-3 text-left border-b font-semibold">Description</th>
                <th className="p-3 text-left border-b font-semibold">HSN/SAC</th>
                <th className="p-3 text-center border-b font-semibold">Qty</th>
                <th className="p-3 text-left border-b font-semibold">Unit</th>
                <th className="p-3 text-right border-b font-semibold">Rate ₹</th>
                <th className="p-3 text-center border-b font-semibold">Tax %</th>
                <th className="p-3 text-right border-b font-semibold">Taxable ₹</th>
                <th className="p-3 text-right border-b font-semibold">CGST ₹</th>
                <th className="p-3 text-right border-b font-semibold">SGST ₹</th>
                <th className="p-3 text-right border-b font-semibold">Total ₹</th>
                <th className="p-3 border-b"></th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, i) => (
                <tr key={i} className="align-top">
                  <td className="p-2 border-b">
                    <input className="w-full border rounded-lg p-2 text-sm"
                      value={item.item_name}
                      onChange={e => updateLineItem(i, "item_name", e.target.value)}
                      placeholder="Item name" />
                    {item.source_stage_item_id && (
                      <span className="text-[10px] text-teal-600 block mt-1">from project</span>
                    )}
                  </td>
                  <td className="p-2 border-b">
                    <input className="w-full border rounded-lg p-2 text-sm"
                      value={item.description}
                      onChange={e => updateLineItem(i, "description", e.target.value)}
                      placeholder="Description" />
                  </td>
                  <td className="p-2 border-b">
                    <input className="w-full border rounded-lg p-2 text-sm"
                      value={item.hsn_code}
                      onChange={e => updateLineItem(i, "hsn_code", e.target.value)}
                      placeholder="HSN" />
                  </td>
                  <td className="p-2 border-b">
                    <input type="number" min="1"
                      className="w-full border rounded-lg p-2 text-sm text-center"
                      value={item.qty}
                      onChange={e => updateLineItem(i, "qty", Number(e.target.value))} />
                  </td>
                  <td className="p-2 border-b">
                    <input className="w-full border rounded-lg p-2 text-sm"
                      value={item.unit}
                      onChange={e => updateLineItem(i, "unit", e.target.value)} />
                  </td>
                  <td className="p-2 border-b">
                    <input type="number" min="0"
                      className="w-full border rounded-lg p-2 text-sm text-right"
                      value={item.rate}
                      onChange={e => updateLineItem(i, "rate", Number(e.target.value))} />
                  </td>
                  <td className="p-2 border-b">
                    <input
                      type="number" min="0" max="100" step="0.01"
                      list="gst-standard-rates"
                      className="w-full border rounded-lg p-2 text-sm text-center"
                      value={item.tax_rate}
                      onChange={e => updateLineItem(i, "tax_rate", Number(e.target.value))}
                      placeholder="0"
                    />
                  </td>
                  <td className="p-2 border-b text-right text-gray-500 pt-4">
                    ₹{Number(item.taxable_amount).toLocaleString()}
                  </td>
                  <td className="p-2 border-b text-right text-gray-500 pt-4">
                    ₹{Number(item.cgst_amount).toFixed(2)}
                  </td>
                  <td className="p-2 border-b text-right text-gray-500 pt-4">
                    ₹{Number(item.sgst_amount).toFixed(2)}
                  </td>
                  <td className="p-2 border-b text-right font-semibold pt-4">
                    ₹{Number(item.amount).toLocaleString()}
                  </td>
                  <td className="p-2 border-b text-center pt-4">
                    <button onClick={() => removeLineItem(i)}
                      disabled={lineItems.length === 1}
                      className="text-red-400 hover:text-red-600 disabled:opacity-30 text-lg">✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
            {/* Live totals row — right inside the table for instant feedback,
                fully automatic (sums every line item's own computed values) */}
            <tfoot>
              <tr className="bg-gray-50 font-semibold">
                <td colSpan={7} className="p-3 text-right text-gray-600">Totals</td>
                <td className="p-3 text-right">₹{subtotal.toLocaleString()}</td>
                <td className="p-3 text-right">₹{totalCgst.toFixed(2)}</td>
                <td className="p-3 text-right">₹{totalSgst.toFixed(2)}</td>
                <td className="p-3 text-right">₹{totalAmount.toLocaleString()}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
        {/* Standard GST slabs — shown as autocomplete suggestions on the Tax %
            input above, but the field itself accepts ANY value (0–100,
            decimals allowed) for genuinely non-standard rates. */}
        <datalist id="gst-standard-rates">
          <option value="0" />
          <option value="5" />
          <option value="12" />
          <option value="18" />
          <option value="28" />
        </datalist>
        <button onClick={addLineItem}
          className="mt-3 px-4 py-2 border-2 border-dashed border-blue-300 text-blue-600 rounded-lg text-sm hover:bg-blue-50">
          + Add Line Item
        </button>
      </div>

      {/* IMPORT FROM PROJECT MODAL */}
      {showImportPanel && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg text-slate-800">📋 Import Items from Project</h3>
              <button onClick={() => setShowImportPanel(false)} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Name, unit, and quantity are pulled from actually-issued material and equipment on this
              project. Rate is always left blank — you set the billing rate yourself for every item.
            </p>
            {loadingImport ? (
              <p className="text-sm text-gray-400">Loading...</p>
            ) : importCandidates.length === 0 ? (
              <p className="text-sm text-gray-400">No issued material or equipment found for this project yet.</p>
            ) : (
              <div className="space-y-2">
                {importCandidates.map(c => {
                  const alreadyInThisForm = lineItems.some(li => li.source_stage_item_id === c.source_stage_item_id);
                  return (
                    <label key={c.key} className={`flex items-center gap-3 p-2 border rounded-lg ${alreadyInThisForm ? "opacity-50 bg-gray-50" : "hover:bg-gray-50 cursor-pointer"}`}>
                      <input type="checkbox"
                        disabled={alreadyInThisForm}
                        checked={Boolean(importSelected[c.key])}
                        onChange={e => setImportSelected(p => ({ ...p, [c.key]: e.target.checked }))}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{c.item_name} <span className="text-gray-400 font-normal">× {c.qty} {c.unit}</span></div>
                        {c.hsn_code && <div className="text-xs text-gray-400">HSN: {c.hsn_code}</div>}
                        {alreadyInThisForm && (
                          <div className="text-xs text-teal-600 font-semibold">✓ Already added to this invoice</div>
                        )}
                        {!alreadyInThisForm && c.alreadyInvoicedIn && (
                          <div className="text-xs text-amber-600 font-semibold">⚠ Already invoiced in {c.alreadyInvoicedIn}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowImportPanel(false)}
                className="flex-1 py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm">
                Cancel
              </button>
              <button onClick={confirmImport}
                className="flex-1 py-3 rounded-xl bg-teal-600 text-white font-semibold text-sm">
                Add Selected Items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOTALS + SAVE — same width as the other cards */}
      <div className="bg-white p-5 rounded-xl shadow">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Discount ₹</label>
              <input type="number" min="0" className="border p-2 rounded w-full text-sm"
                value={form.discount}
                onChange={e => setForm({ ...form, discount: Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Paid Amount ₹</label>
              <input type="number" min="0" className="border p-2 rounded w-full text-sm"
                value={form.paid_amount}
                onChange={e => setForm({ ...form, paid_amount: Number(e.target.value) })} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 block mb-1">Notes</label>
              <textarea rows={2} className="border p-2 rounded w-full text-sm resize-none"
                placeholder="Optional notes..."
                value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Subtotal</span>
              <span>₹{subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Discount</span>
              <span>- ₹{Number(form.discount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">CGST</span>
              <span>₹{totalCgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">SGST</span>
              <span>₹{totalSgst.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-400 text-xs">
              <span>Total Tax</span>
              <span>₹{taxAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-base border-t pt-2">
              <span>Total</span><span>₹{totalAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-green-700">
              <span>Paid</span><span>₹{Number(form.paid_amount).toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-red-700 font-semibold">
              <span>Balance</span><span>₹{balanceAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                paymentStatus === "Paid"    ? "bg-green-100 text-green-700" :
                paymentStatus === "Partial" ? "bg-blue-100 text-blue-700"  :
                                             "bg-yellow-100 text-yellow-700"
              }`}>{paymentStatus}</span>
            </div>
            {/* TO-8: AMC next date preview */}
            {amcContract && (
              <div className="mt-3 pt-3 border-t border-purple-100">
                <p className="text-xs text-purple-600 font-semibold">After saving:</p>
                <p className="text-xs text-purple-500">
                  Next AMC invoice date →{" "}
                  <strong>
                    {calcNextInvoiceDate(
                      amcContract.next_invoice_date || amcContract.start_date,
                      amcContract.frequency
                    )}
                  </strong>
                </p>
              </div>
            )}
          </div>
        </div>

        <button onClick={saveInvoice} disabled={saving}
          className="mt-6 w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm disabled:opacity-50 transition">
          {saving
            ? "Saving..."
            : editId
            ? "Update Invoice"
            : amcContract
            ? `💾 Save AMC Invoice & Advance to ${calcNextInvoiceDate(amcContract.next_invoice_date || amcContract.start_date, amcContract.frequency)}`
            : "Save Invoice"
          }
        </button>
      </div>
{/* QUICK CREATE — Client */}
      <QuickCreateModal
        open={qcOpen === "client"}
        onClose={() => setQcOpen(null)}
        title="Quick Create — Client"
      >
        <QuickCreateClient
          companyId={company_id}
          onCreated={(newClient) => {
            setClients(prev => [...prev, newClient]);
            setForm(f => ({
              ...f,
              client_id:   newClient.id,
              client_name: newClient.client_name,
              project_id: "", project_name: "", site_id: "", site_name: "",
            }));
            fetchSites(newClient.id);
            fetchProjects(newClient.id);
            setQcOpen(null);
          }}
          onCancel={() => setQcOpen(null)}
        />
      </QuickCreateModal>

      {/* QUICK CREATE — Site */}
      <QuickCreateModal
        open={qcOpen === "site"}
        onClose={() => setQcOpen(null)}
        title="Quick Create — Site"
      >
        <QuickCreateSite
          companyId={company_id}
          clientId={form.client_id}
          clientName={form.client_name}
          onCreated={(newSite) => {
            setSites(prev => [...prev, newSite]);
            setForm(f => ({
              ...f,
              site_id:   newSite.id,
              site_name: newSite.site_name,
            }));
            setQcOpen(null);
          }}
          onCancel={() => setQcOpen(null)}
        />
      </QuickCreateModal>

    {/* ── Duplicate Invoice Warning Modal ── */}
      {dupWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-3xl">⚠️</span>
              <div>
                <h3 className="font-bold text-slate-800 text-lg">Duplicate Invoice Warning</h3>
                <p className="text-sm text-slate-500 mt-1">
                  An invoice already exists for this service job.
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-sm">
              <p className="font-semibold text-amber-800">Existing Invoice:</p>
              <p className="text-amber-700 mt-1">
                {dupWarning.invoice_no} — {dupWarning.client_name} —
                ₹{Number(dupWarning.total_amount).toLocaleString("en-IN")}
              </p>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              Creating another invoice may result in double billing. Are you sure?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDupWarning(null)}
                className="flex-1 py-3 rounded-xl bg-slate-100 text-slate-700 font-semibold text-sm hover:bg-slate-200 transition">
                Cancel — Go Back
              </button>
              <button
                onClick={() => { setDupWarning(null); saveInvoice(true); }}
                className="flex-1 py-3 rounded-xl bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition">
                Create Anyway
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}